<template>
  <googlemaps-map
    ref="map"
    data-cy="traffic-map"
    :center.sync="center"
    :zoom.sync="zoom"
    :options="mapOptions"
    class="traffic-map"
    @click.self="closePopup"
  >
    <EntityMarker
      v-for="(heartbeat, key, i) of heartbeats"
      :key="heartbeat.username"
      :username="heartbeat.username"
      :position="{
        lat: heartbeat.latitude,
        lng: heartbeat.longitude,
      }"
      :z-index="heartbeat.username === popupId ? maxZIndex : i"
      @click="togglePopup"
      @update="updatePopupPosition"
    />
    <EntityMarker
      v-if="lastKnownHeartbeat"
      :key="lastKnownHeartbeat.username"
      :username="lastKnownHeartbeat.username"
      :position="{
        lat: lastKnownHeartbeat.latitude,
        lng: lastKnownHeartbeat.longitude,
      }"
      :z-index="lastKnownHeartbeat.username === popupId ? maxZIndex : i"
      @click="togglePopup"
    />
  </googlemaps-map>
</template>

<script>
/* global google */
import Vue from 'vue';
import { mapState } from 'vuex';
import eventBus from '@/eventBus.js';

import store from '@/store';
import i18n from '@/i18n';

import heartbeatSocketMixin from '@/mixins/heartbeatSocketMixin';

import Popup from './Popup';
import EntityPopover from '../common/EntityPopover';
import EntityMarker from './EntityMarker';

export default {
  components: {
    EntityMarker,
  },

  mixins: [heartbeatSocketMixin],

  data() {
    return {
      center: {
        lat: parseFloat(process.env.VUE_APP_MAP_DEFAULT_LATITUDE),
        lng: parseFloat(process.env.VUE_APP_MAP_DEFAULT_LONGITUDE),
      },
      zoom: parseFloat(process.env.VUE_APP_MAP_DEFAULT_ZOOM),
      mapOptions: {
        mapTypeControlOptions: {
          position: 6,
          mapTypeIds: ['roadmap', 'hybrid'],
        },
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            stylers: [{ visibility: 'on' }],
          },
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text',
            stylers: [{ visibility: 'off' }],
          },
        ],
      },
      initialFitDone: false,
      popup: null,
      popupId: null,
      popupVueInstance: null,
    };
  },

  computed: {
    ...mapState('live', ['heartbeats', 'lastKnownHeartbeat']),

    maxZIndex() {
      return google ? google.maps.Marker.MAX_ZINDEX + 1 : 100000;
    },
  },

  mounted() {
    eventBus.$on('panTo', this.handlePanTo);
  },

  beforeDestroy() {
    eventBus.$off('panTo', this.handlePanTo);
  },

  methods: {
    /**
     * Center the map with animation when global event `panTo` is fired.
     */
    handlePanTo(location) {
      this.$refs.map.panTo(location);
    },

    /**
     * Display a popup on the map for the given user at the given position
     */
    togglePopup({ username, position }) {
      const previousPopupId = this.popupId;

      // Hide any other popup
      this.closePopup();

      // We just hide the popup that was previously displayed
      // Let's stop here to not show it again
      if (previousPopupId === username) {
        return;
      }

      this.popup = new Popup(position);
      this.popupId = username;

      // We want the popup to follow the marker when the map moves.
      // For this reason we have to render it within a Google Maps OverlayView abstracted as the Popup constructor here.
      // Since we cannot render child Vue components in a separate root node, let's create a new instance.
      this.popupVueInstance = new Vue({
        store,
        i18n,
        render: h =>
          h(EntityPopover, {
            props: {
              username,
            },
          }),
      });

      this.popup.setMap(this.$refs.map.$_map);
      this.popupVueInstance.$mount(this.popup.popupNode);
    },

    closePopup() {
      if (this.popup) {
        this.popup.setMap(null);
        this.popup = null;
        this.popupId = null;
      }
      if (this.popupVueInstance) {
        this.popupVueInstance.$destroy();
      }
    },

    updatePopupPosition({ username, position }) {
      if (this.popup && this.popupId === username) {
        this.popup.setPosition(position);
      }
    },
  },
};
</script>
